class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


// Luvella: apply a code without leaving the cart drawer.
if (!window.luvellaCartDiscountBound) {
  window.luvellaCartDiscountBound = true;
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-luvella-discount-form]');
    if (!form) return;
    event.preventDefault();
    const input = form.querySelector('[name="discount"]');
    const status = form.querySelector('[data-discount-status]');
    const remove = event.submitter && event.submitter.name === 'remove_discount';
    const code = remove ? '' : input.value.trim();
    if (!remove && !code) {
      status.textContent = 'Bitte gib einen Rabattcode ein.';
      input.setAttribute('aria-invalid', 'true');
      input.focus();
      return;
    }
    input.removeAttribute('aria-invalid');
    form.setAttribute('aria-busy', 'true');
    form.querySelectorAll('button').forEach(button => button.disabled = true);
    status.textContent = 'Rabatt wird geprüft …';
    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ discount: code, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname })
      });
      const cart = await response.json();
      if (!response.ok || cart.errors || cart.status >= 400) throw new Error('discount request failed');
      const applications = [
        ...(cart.cart_level_discount_applications || []),
        ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))
      ].filter(Boolean);
      const accepted = (cart.discount_codes || []).some(discount => discount.code?.toUpperCase() === code.toUpperCase() && discount.applicable === true)
        || applications.some(application => String(application.title || '').toUpperCase() === code.toUpperCase());
      const message = remove ? 'Rabattcode wurde entfernt.' : accepted
        ? 'Rabattcode ' + code.toUpperCase() + ' wurde angewendet. Die Summe ist aktualisiert.'
        : 'Dieser Code ist für deinen Warenkorb nicht anwendbar. Prüfe die Schreibweise und Bedingungen. Bundlepreise sind nicht zusätzlich rabattierbar.';
      const drawer = document.querySelector('cart-drawer');
      if (!cart.sections?.['cart-drawer'] || !cart.sections?.['cart-icon-bubble'] || !drawer?.renderContents) throw new Error('cart sections unavailable');
      drawer.close();
      drawer.renderContents(cart);
      requestAnimationFrame(() => {
        const updated = drawer.querySelector('[data-luvella-discount-form]');
        if (!updated) return;
        updated.querySelector('[data-discount-status]').textContent = message;
        const updatedInput = updated.querySelector('[name="discount"]');
        updatedInput.value = remove ? '' : code;
        if (!remove && !accepted) updatedInput.setAttribute('aria-invalid', 'true');
        updatedInput.focus({ preventScroll: true });
      });
    } catch (error) {
      status.textContent = 'Der Rabatt konnte gerade nicht geprüft werden. Bitte versuche es erneut oder gib den Code beim Checkout ein.';
    } finally {
      form.removeAttribute('aria-busy');
      form.querySelectorAll('button').forEach(button => button.disabled = false);
    }
  });
}
